import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, X, User, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useCatalog } from "../lib/catalog";
import { useSubcategories } from "../lib/hooks";

// Static top-level nav order (from client's mega menu spec)
const TOP_NAV = [
  { slug: "shop", label: "Shop", static: true },
  { slug: "everyday-carry", label: "Everyday Carry" },
  { slug: "workspace", label: "Workspace" },
  { slug: "travel", label: "Travel" },
  { slug: "home-essentials", label: "Home Essentials" },
  { slug: "gift-ideas", label: "Gift Ideas" },
];

const SHOP_QUICK = [
  { label: "Shop All Products", to: "/shop" },
  { label: "New Arrivals", to: "/shop?sort=new" },
  { label: "Best Sellers", to: "/shop?filter=bestseller" },
  { label: "Trending Products", to: "/shop?filter=trending" },
];
const SHOP_PRICE = [
  { label: "Under ₹299", to: "/shop?max=299" },
  { label: "₹300 – ₹499", to: "/shop?min=300&max=499" },
  { label: "₹500 – ₹999", to: "/shop?min=500&max=999" },
  { label: "Above ₹999", to: "/shop?min=1000" },
];

export default function Header() {
  const { count } = useCart();
  const { count: wcount } = useWishlist();
  const { categories } = useCatalog();
  const allSubs = useSubcategories();
  const [drawer, setDrawer] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
    setQ("");
  };

  const subsFor = (cat) => allSubs.filter((s) => s.category === cat);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200" data-testid="site-header">
      <div className="bg-neutral-900 text-white text-[10px] md:text-xs tracking-wide">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2 flex items-center justify-center gap-3 md:gap-6 text-center">
          <span className="font-medium">FREE SHIPPING OVER ₹499</span>
          <span className="opacity-60">•</span>
          <span>COD · 6-MO WARRANTY</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setDrawer(true)} data-testid="mobile-menu-toggle">
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#FF5A1F] flex items-center justify-center text-white font-black text-lg">S</div>
            <div className="leading-none">
              <div className="font-display text-xl md:text-2xl font-black tracking-tight text-neutral-900">Stealodeal</div>
              <div className="hidden md:block text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-0.5">Everyday Carry Co.</div>
            </div>
          </Link>

          {/* Mega nav */}
          <nav
            className="hidden lg:flex items-center gap-1 text-sm font-medium text-neutral-700"
            onMouseLeave={() => setOpenMenu(null)}
            data-testid="mega-nav"
          >
            {TOP_NAV.map((n) => {
              const subs = subsFor(n.slug);
              const hasMega = n.static || subs.length > 0;
              return (
                <div
                  key={n.slug}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(hasMega ? n.slug : null)}
                >
                  <NavLink
                    to={n.slug === "shop" ? "/shop" : `/shop/${n.slug}`}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 px-3 py-2 rounded-full transition hover:text-[#FF5A1F] ${isActive ? "text-neutral-900 font-semibold" : ""}`
                    }
                    data-testid={`nav-${n.slug}`}
                  >
                    {n.label}
                    {hasMega && <ChevronDown className="w-3.5 h-3.5" />}
                  </NavLink>
                </div>
              );
            })}
            <Link to="/orders" className="px-3 py-2 hover:text-[#FF5A1F]" data-testid="nav-orders">My Orders</Link>
            <Link to="/about" className="px-3 py-2 hover:text-[#FF5A1F]">About</Link>
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <button onClick={() => setSearchOpen((v) => !v)} className="p-2 hover:bg-neutral-100 rounded-full transition" data-testid="search-toggle"><Search className="w-5 h-5" /></button>
            <Link to="/wishlist" className="p-2 hover:bg-neutral-100 rounded-full transition relative" data-testid="wishlist-link">
              <Heart className="w-5 h-5" />
              {wcount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF5A1F] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{wcount}</span>}
            </Link>
            <button className="p-2 hover:bg-neutral-100 rounded-full transition hidden md:block" data-testid="account-link"><User className="w-5 h-5" /></button>
            <Link to="/cart" className="p-2 hover:bg-neutral-100 rounded-full transition relative" data-testid="cart-link">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF5A1F] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
            </Link>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={onSearch} className="pb-4">
            <div className="flex gap-2 border border-neutral-300 rounded-full overflow-hidden px-5 h-11 focus-within:border-neutral-900 transition">
              <Search className="w-4 h-4 self-center text-neutral-500" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search card holders, wallets, keychains..." className="flex-1 outline-none text-sm bg-transparent" data-testid="search-input" />
              <button type="submit" className="text-sm font-semibold text-[#FF5A1F]" data-testid="search-submit">Search</button>
            </div>
          </form>
        )}
      </div>

      {/* Mega Menu Panel */}
      {openMenu && (
        <div
          className="hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-neutral-200 shadow-xl"
          onMouseEnter={() => setOpenMenu(openMenu)}
          onMouseLeave={() => setOpenMenu(null)}
          data-testid={`megamenu-${openMenu}`}
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 grid grid-cols-4 gap-8">
            {openMenu === "shop" ? (
              <>
                <MegaCol title="Shop">
                  {SHOP_QUICK.map((x) => <MegaLink key={x.label} to={x.to} label={x.label} onClick={() => setOpenMenu(null)} />)}
                </MegaCol>
                <MegaCol title="By Price">
                  {SHOP_PRICE.map((x) => <MegaLink key={x.label} to={x.to} label={x.label} onClick={() => setOpenMenu(null)} />)}
                </MegaCol>
                <MegaCol title="Categories">
                  {categories.slice(0, 6).map((c) => <MegaLink key={c.slug} to={`/shop/${c.slug}`} label={c.name} onClick={() => setOpenMenu(null)} />)}
                </MegaCol>
                <MegaFeatured />
              </>
            ) : (
              <>
                <div className="col-span-3 grid grid-cols-3 gap-6">
                  <MegaCol title={TOP_NAV.find((t) => t.slug === openMenu)?.label}>
                    <MegaLink to={`/shop/${openMenu}`} label="View all" onClick={() => setOpenMenu(null)} bold />
                    {subsFor(openMenu).slice(0, 8).map((s) => (
                      <MegaLink key={s.slug} to={`/shop/${openMenu}?sub=${s.slug}`} label={s.name} onClick={() => setOpenMenu(null)} />
                    ))}
                  </MegaCol>
                  {subsFor(openMenu).length > 8 && (
                    <MegaCol title="More">
                      {subsFor(openMenu).slice(8).map((s) => (
                        <MegaLink key={s.slug} to={`/shop/${openMenu}?sub=${s.slug}`} label={s.name} onClick={() => setOpenMenu(null)} />
                      ))}
                    </MegaCol>
                  )}
                </div>
                <MegaFeatured />
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[86%] max-w-sm bg-white flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-neutral-200">
              <span className="font-display font-black text-xl">Menu</span>
              <button onClick={() => setDrawer(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 overflow-auto p-2">
              <NavLink to="/" onClick={() => setDrawer(false)} className="block px-4 py-3 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50">Home</NavLink>
              {TOP_NAV.map((n) => (
                <details key={n.slug} className="border-b border-neutral-100 last:border-0">
                  <summary className="px-4 py-3 text-sm font-semibold cursor-pointer list-none flex justify-between items-center">
                    {n.label}
                    <ChevronDown className="w-4 h-4" />
                  </summary>
                  <div className="pb-2 pl-4">
                    <Link to={n.slug === "shop" ? "/shop" : `/shop/${n.slug}`} onClick={() => setDrawer(false)} className="block px-4 py-2 text-sm text-[#FF5A1F] font-semibold">View all →</Link>
                    {subsFor(n.slug).map((s) => (
                      <Link key={s.slug} to={`/shop/${n.slug}?sub=${s.slug}`} onClick={() => setDrawer(false)} className="block px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">{s.name}</Link>
                    ))}
                  </div>
                </details>
              ))}
              <NavLink to="/orders" onClick={() => setDrawer(false)} className="block px-4 py-3 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50">My Orders</NavLink>
              <NavLink to="/about" onClick={() => setDrawer(false)} className="block px-4 py-3 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50">About</NavLink>
              <NavLink to="/contact" onClick={() => setDrawer(false)} className="block px-4 py-3 rounded-lg text-sm font-medium text-neutral-700 hover:bg-neutral-50">Contact</NavLink>
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
}

function MegaCol({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-neutral-500 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function MegaLink({ to, label, onClick, bold }) {
  return (
    <Link to={to} onClick={onClick} className={`block text-sm hover:text-[#FF5A1F] transition ${bold ? "font-bold text-neutral-900" : "text-neutral-600"}`}>
      {label}
    </Link>
  );
}

function MegaFeatured() {
  return (
    <div className="rounded-2xl bg-neutral-900 text-white p-6 flex flex-col justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#FF5A1F]">This Month</div>
        <h3 className="mt-2 font-display text-xl font-black leading-tight">Card Holders from ₹105</h3>
        <p className="text-xs text-neutral-400 mt-1">RFID-protected · Built to last.</p>
      </div>
      <Link to="/shop/card-holders" className="mt-4 inline-block text-sm font-bold text-[#FF5A1F] hover:text-white">
        Shop Now →
      </Link>
    </div>
  );
}
